<template>
  <!-- Golem fork (FT-1101): THE NIGHT ASKING THIS SEAT FOR SOMETHING.

       FT-1005 built this surface and ChroniclesNights owned it, inside the
       moon filter of the chronicles drawer. The user's report from a live
       8-seat game — "the Imp isn't given an option to kill someone" — was
       never that the controls were wrong: they were behind a filter cell the
       player had no reason to press, in a drawer whose default view is the
       message stream, with nothing anywhere saying the night wanted them.

       So the surface came out into its own component and now stands in TWO
       rooms with one definition: the nights view, where it always was, and
       PINNED at the foot of the chronicles stream, where a player already is.
       Same commits (night/playerAction), same echo round trip, same schema
       reads — only the number of rooms changed.

       `pinned` is the band form: it names the night and wears its own frame,
       because in the stream it is an interruption rather than a section.

       ── FT-1107 (user): AND BOTH OF THOSE ROOMS WERE THE WRONG ROOM. ──

       "The interaction should happen on the clock face. not in the chat, the
       chronicle should just record what was done." A player sat as a Fortune
       Teller with the drawer shut, watched their own coin light up on the
       ring, and had nowhere to answer — because the answer was inside a
       drawer, and a drawer is somewhere you go, not somewhere you are.

       So there is a THIRD form, `face`, and it is now the only one that
       renders: the ask, on the town square itself. It is deliberately not a
       copy of the band. The band had to be self-sufficient (it stood in a
       stream of unrelated talk, so it carried a moon, a heading, its own
       frame and a privacy line); the face form stands ON the thing it is
       talking about, so it carries the words and nothing else — the SEATS
       are the control (Player.vue's night-pick overlay), and the picked
       coins are the readout. `SeatPicker` never appears here: a dropdown
       listing seat numbers, laid over a ring of those same seats, is the
       chat-shaped control this whole card was filed to get rid of.

       `action.freeText` is the exception, and stays an input, because a
       character nobody designed controls for has no seats to point at.

       One component, three dresses, ONE question — `night/myCall` — so the
       face and the seats can never disagree about whether this player is
       being asked. -->
  <!-- ── FT-1113 (user): THE ASK TAKES THE MIDDLE ─────────────────────────

       "Move the ask to the middle and let the counts yield while you're
       being asked. Make the answer the loudest thing on the face. But keep
       the name chips. make them blue though."

       FT-1107 hung this form BELOW the counts — deliberately, so nothing
       above it moved at dusk. The cost was that the one thing the player
       must ACT on sat underneath a stack of storyteller bookkeeping, at the
       narrowest part of the disc. It stands in the dial's own centre now
       (TownInfo owns the move; see `.info-night` there), and the two things
       that changed HERE are hierarchy, not position:

         `told`   once the storyteller's answer has landed, the instruction
                  that got the player this far steps back — it is spent, and
                  the answer is the whole point of the turn.
         the chips wear the townsfolk blue instead of the coin's gold. -->
  <div class="nc" :class="{ pinned, face, told: face && answer.length > 0 }">
    <!-- ── THE FACE FORM (FT-1107) ─────────────────────────────────────── -->
    <template v-if="face">
      <!-- FT-1272 (user): THE HEADER LEADS WITH THE CHARACTER, then names it
           in the character's OWN TEAM COLOUR.
             · the icon, because a player who has been handed a character they
               have never played reads the picture faster than the word, and
               the ring below is already all pictures — the header was the one
               place on this face that was text alone.
             · the team, because "am I good or evil" is the second thing after
               "who am I", and the face had been saying it in the same gold
               every other line wears. See `.nf-role`'s note below for why the
               colour is the raw token rather than a wash. -->
      <span class="nf-role" :class="'team-' + teamOf">
        <span
          class="nf-role-icon"
          :style="{ backgroundImage: `url(${iconUrl})` }"
        ></span>
        {{ action.role.name }}
      </span>
      <span class="nf-line">{{ action.line }}</span>
      <!-- WHAT TO DO, in the imperative, naming the clock: the player has to
           learn once that the ring is the control. It counts DOWN as they
           pick, so the line is also the progress readout — one element doing
           both, because the hub has room for neither twice. -->
      <!-- FT-1291: ...and once the storyteller has SENT, this same line is
           what says so. It is the only line on the face that was ever going
           to carry it: the instruction and the report are already one element
           here, and "the choice is closed" is the last thing in that series.
           A control that stops answering with nothing said is the failure the
           night sheet argues against at its own ping dropdown; the ring goes
           quiet at the same moment these words change, so the player is told
           why rather than left tapping. -->
      <!-- FT-1384: `settled` follows the shared lock, not only the send —
           the line stands down to its quiet parchment voice the moment the
           choice seals, whichever side sealed it. -->
      <!-- FT-1399: gated on the line's own text too — a dressed chooser's
           stage-complete beat hands the whole sentence to the Confirm below
           ("Confirm Maeve"), and an empty span would still paint the gap. -->
      <!-- FT-1403 phase 2: which copy key wrote this sentence — the Night
           lab's editor outlines the line whose box has focus. Inert
           otherwise; nothing reads it but that one unscoped rule. -->
      <span
        class="nf-ask"
        :class="{ settled: locked }"
        :data-night-copy="locked ? 'sealed' : 'invite'"
        v-if="action.slots && askLine"
        >{{ askLine }}</span
      >
      <!-- FT-1399 vet 2 (user): the chips yield wherever the verb lines
           already name the pick — a name never appears twice in one state.
           `chipsShown` says which states those are. -->
      <span
        class="nf-chosen"
        v-if="action.slots && chosenNames.length && chipsShown"
      >
        <span class="nf-chip" v-for="(n, i) in chosenNames" :key="'c' + i">{{
          n
        }}</span>
      </span>
      <!-- FT-1384: THE CONFIRM — the player's commit, standing where the
           vote hands stand (the face centre; .vo-solo's slot, one surface
           over). It exists only in the window between "every slot staged"
           and "sealed": before that there is nothing to confirm, after it
           there is nothing left to press. Pressing it is the ONE wire event
           of the whole choice — the staged picks go up as today's
           playerAction frame, the role's seal plays on the coins, and the
           choice locks until the storyteller re-asks. -->
      <!-- FT-1399 (vet 2): a dressed chooser's button carries the pick by
           NAME — "Confirm Maeve", live as the staged pick moves — and it
           is the one naming of the pick in this state (the chips above
           yield to it). The verb lives in the invite and the sealed line,
           where it is true. An undressed chooser keeps the bare word. -->
      <button
        class="nf-confirm"
        v-if="action.slots && stageComplete && !locked"
        @click="confirm"
      >
        {{ confirmLabel }}
      </button>
      <!-- The universal fallback: no seats to point at, so their own words.
           A plain `v-if`, not a `v-else-if` on the row above — `freeText` is
           by construction only ever true when there are no player slots (both
           read the same schema table, and a character absent from it has
           neither), so the chain bought nothing and would have broken the
           moment a comment or an element landed between the two. -->
      <input
        v-if="action.freeText"
        type="text"
        class="nd-free nf-free"
        :placeholder="
          sent
            ? 'The storyteller has answered'
            : 'Your choice, in your own words'
        "
        spellcheck="false"
        maxlength="280"
        :disabled="sent"
        :title="sent ? sentLine : ''"
        :value="freeTextValue"
        @input="typeText($event.target.value)"
        @blur="flushText"
      />
      <!-- FT-1291: the words path needs the line too. `action.freeText` and
           `action.slots` are mutually exclusive by construction (see the note
           above), so the ask line above never renders on this branch and this
           seat would otherwise have a dead box and no sentence. -->
      <span class="nf-ask settled" v-if="action.freeText && sent">{{
        sentLine
      }}</span>
      <!-- AND THE ANSWER LANDS HERE TOO. The storyteller's echo — the
           Fortune Teller's "Yes", the Empath's number, the character an
           Undertaker was shown — read where the question was asked, so
           nothing has to be opened to hear it. -->
      <!-- FT-1385: `toldNumberDress` — a told COUNT role's numeral wears
           its role's own ring (the Chef's scorch brand): the number never
           sits bare again. -->
      <span class="nf-told" v-if="answer.length" :class="toldNumberDress">
        <!-- FT-1401 v2 (user): the Empath's ZERO is her heart — the coin
             ledger's own glyph stands in for the "0" chip, sized like it
             (a bare nought said less than the heart the ledger already
             speaks for a clean night). Every other chip is untouched. -->
        <span
          class="nd-told"
          v-for="(a, i) in answer"
          :key="'a' + i"
          :class="[a.cls, { 'nd-heart': heartChip(a) }]"
        >
          <!-- FT-1401 v4 (user): r2 heart back — round 3's grown-copy
               heart (NightMark's em-led-heart verbatim) retired
               kept-commented per never-delete:
          <svg
            v-if="heartChip(a)"
            class="nd-heart-glyph"
            viewBox="-9.6 -9.9 19.2 19.2"
            aria-hidden="true"
          >
            <path
              d="M 0 6.3 C -7.2 -1.1 -5.8 -8.8 0 -4.9 C 5.8 -8.8 7.2 -1.1 0 6.3 Z"
            />
          </svg>
          -->
          <!-- what stands is round 2's own heart glyph — the coin's chip
               is a fixed-size box (1.45em, below) so this shape reads at
               the same on-screen size the grown copy did. -->
          <svg
            v-if="heartChip(a)"
            class="nd-heart-glyph"
            viewBox="-5.2 -5.7 10.4 10.4"
            aria-hidden="true"
          >
            <path
              d="M 0 3.6 C -4.1 -0.6 -3.3 -5 0 -2.8 C 3.3 -5 4.1 -0.6 0 3.6 Z"
            />
          </svg>
          <template v-else>
            {{ a.text }}
            <!-- FT-1401 v4 (user): drips retired — red alone. THE BLOOD
                 RUNS overlay (drawn drips, path data verbatim from
                 NightMark's em-led-blood) kept-commented per never-delete:
            <svg
              v-if="bloodChip(a)"
              class="nd-blood"
              viewBox="0 0 100 100"
              aria-hidden="true"
            >
              <g transform="translate(50 62) scale(5.4)">
                <path
                  class="ndb-drip"
                  d="M -2.05 -0.8 C -2.15 1 -2.12 2.4 -1.88 3.55
                     C -1.8 3.95 -1.5 3.95 -1.43 3.55
                     C -1.25 2.4 -1.32 1 -1.3 -0.8 Z"
                />
                <circle class="ndb-drop" cx="-1.66" cy="4" r="0.62" />
                <circle class="ndb-dtip" cx="-1.66" cy="4.18" r="0.4" />
                <path
                  class="ndb-drip"
                  d="M 1.25 -0.8 C 1.18 0.2 1.2 1.15 1.4 2
                     C 1.47 2.32 1.72 2.32 1.78 2
                     C 1.95 1.15 1.9 0.2 1.9 -0.8 Z"
                />
                <circle class="ndb-drop" cx="1.58" cy="2.4" r="0.5" />
                <circle class="ndb-dtip" cx="1.58" cy="2.54" r="0.32" />
              </g>
            </svg>
            -->
          </template>
        </span>
      </span>
      <!-- FT-1385: THE TELLING'S SENTENCE — the told-information roles'
           answer arrives wearing its meaning: "one of these two is the
           Chef" under the delivered chip, pointing at the two coins the
           marks have just acknowledged. Settles with the rest of the dress
           into the residue's smaller line, which NAMES the pair — the
           sentence a player re-reads at four in the morning. Tonight's
           telling only: the face is the night's own surface, and an older
           night's residue lives on the coins, not here. -->
      <span
        class="nf-sentence"
        v-if="toldSentenceText"
        :class="['nfs-' + told.roleId, { settled: toldSettled }]"
        :data-night-copy="toldSettled ? 'settled' : 'telling'"
        >{{ toldSentenceText }}</span
      >
      <!-- FT-1385: THE LIBRARIAN'S BOOK — open beneath the sentence while
           the telling is bright (blank pages on the zero-Outsiders night),
           CLOSED once it settles: one soft shut, the blue bookmark left
           sticking out. The place is kept; the coins' page-corners say
           where. -->
      <svg
        class="nf-book"
        v-if="toldBook"
        viewBox="0 0 60 30"
        :class="{ settled: toldSettled }"
        aria-hidden="true"
      >
        <template v-if="!toldSettled">
          <g class="bk-open">
            <path
              class="bk-page"
              d="M 30 24 Q 16 28 4 20 L 4 8 Q 16 15 30 11 Z"
            />
            <path
              class="bk-page"
              d="M 30 24 Q 44 28 56 20 L 56 8 Q 44 15 30 11 Z"
            />
            <path class="bk-spine" d="M 30 11 L 30 24" />
            <template v-if="!toldZero">
              <path class="bk-lines" d="M 9 13 Q 18 17 26 15" />
              <path class="bk-lines" d="M 34 15 Q 42 17 51 13" />
            </template>
          </g>
        </template>
        <template v-else>
          <g class="bk-closed">
            <path
              class="bk-cover"
              d="M 8 10 L 52 10 Q 54 10 54 12 L 54 22 Q 54 24 52 24
                 L 8 24 Q 6 24 6 22 L 6 12 Q 6 10 8 10 Z"
            />
            <path class="bk-pages" d="M 8 13.5 L 52 13.5" />
            <path
              class="bk-mark"
              d="M 42 10 L 42 3 L 45 6.5 L 48 3 L 48 10 Z"
            />
          </g>
        </template>
      </svg>
    </template>
    <!-- ── THE BAND / SECTION FORM (FT-1005, FT-1101) ───────────────────── -->
    <template v-else>
      <span class="nc-head" v-if="pinned">
        <img class="nc-moon" :src="moonFull" alt="" />
        Night {{ day }} — the night is asking you
      </span>
      <div class="nd-row nd-live">
        <span class="nd-role">{{ action.role.name }}</span>
        <span class="nd-line">{{ action.line }}</span>
        <!-- their OWN choices — only the fields the schema says the player
             fills (playerSlots); the storyteller's pointings never render an
             input here -->
        <div class="nd-input" v-if="action.slots">
          <span class="nd-your">{{
            action.slots > 1 ? "Your choices:" : "Your choice:"
          }}</span>
          <!-- FT-1291: locked once the row has been sent, on the picker's own
               `disabled` — the prop FT-1272 put there for the night sheet's
               half of exactly this rule. It rides the trigger's native
               `disabled` rather than a guard in the handler, so the popup
               cannot be opened by keyboard either. -->
          <SeatPicker
            v-for="slot in action.slots"
            :key="'t' + slot"
            class="nd-pick"
            :players="players"
            :picked-seat="slotValue(slot - 1)"
            :disabled="sent"
            :title="
              sent
                ? sentLine
                : 'Your own pick (' +
                  slot +
                  ' of ' +
                  action.slots +
                  ') — the storyteller sees it as yours'
            "
            @pick="(seat) => pickSeat(slot - 1, seat)"
          />
          <span class="nd-settled" v-if="sent">{{ sentLine }}</span>
        </div>
        <!-- the universal fallback where no control was ever designed for this
             character: their choice in their own words -->
        <div class="nd-input" v-else-if="action.freeText">
          <input
            type="text"
            class="nd-free"
            :placeholder="
              sent
                ? 'The storyteller has answered'
                : 'Your choice, in your own words'
            "
            spellcheck="false"
            maxlength="280"
            :disabled="sent"
            :title="sent ? sentLine : ''"
            :value="freeTextValue"
            @input="typeText($event.target.value)"
            @blur="flushText"
          />
          <span class="nd-settled" v-if="sent">{{ sentLine }}</span>
        </div>
        <!-- what the storyteller has answered so far — everything on the row
             except whether it was a lie, live as they fill it -->
        <template v-if="row">
          <span
            class="nd-told"
            :class="pingClass(row)"
            v-if="row.ping !== null"
          >
            {{ row.ping ? "Yes" : "No" }}
          </span>
          <span
            class="nd-told"
            v-if="row.number !== null && row.number !== undefined"
          >
            {{ row.number }}
          </span>
          <span class="nd-told" v-if="row.characterName">{{
            row.characterName
          }}</span>
          <span class="nd-text" v-if="row.text">{{ row.text }}</span>
        </template>
      </div>
      <span class="nc-privacy" v-if="pinned"
        >Only you and the storyteller see this.</span
      >
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SeatPicker from "./SeatPicker";
import moonFull from "../assets/moon-full.png";
import { roleIconUrl } from "../golem/roleIcon";
// FT-1385: the told-information dress — the seats a telling acknowledges.
import { toldSeats } from "../golem/toldInfo";
// FT-1403 phase 1: which told roles' dress has landed, and which furniture
// this role's dress wants (the number-chip dress, the book, the ledger
// glyphs) — the night signs registry's flags, replacing the role-id
// branches this file carried. The registry is also where the SENTENCES
// come from now: verbsFor/toldSentenceFor are the FT-1399 verb templates
// and the FT-1385 told sentences read through the script's own nightCopy
// override layer (COPY AS DATA — a custom script may reword a role's
// invite/sealed/telling lines; without a section, both answer exactly what
// chooserVerbsFor/toldSentence always did).
// enabled() is the FT-1404 "Show night signs" gate — on this face it
// stands down the DRESS alone (the number-chip treatments, the book, the
// ledger glyphs); the sentences, the chips' content and every control are
// function and never consult it.
import {
  enabled,
  hasToldArt,
  signFlag,
  verbsFor,
  toldSentenceFor,
} from "../golem/nightSigns";

/** FT-1272: the teams this face has a colour for (see `.nf-role`'s SCSS map —
 *  the two must stay in step, and they are both built off vars.scss's tokens).
 *  Anything else floors to townsfolk rather than rendering an unstyled name. */
const TEAMS = [
  "townsfolk",
  "outsider",
  "minion",
  "demon",
  "traveler",
  "fabled",
];

export default {
  name: "NightCall",
  components: { SeatPicker },
  props: {
    /** tonightActionFor()'s answer — never null; the parent gates on it. */
    action: {
      type: Object,
      required: true,
    },
    /** This seat's delivered row for tonight, or null before the host has
     *  echoed anything. The echo is the truth; nothing here is optimistic. */
    row: {
      type: Object,
      default: null,
    },
    /** Which night, for the band's own heading. */
    day: {
      type: Number,
      default: 0,
    },
    /** The pinned band form (the stream's foot) rather than the section form
     *  (the nights view). */
    pinned: {
      type: Boolean,
      default: false,
    },
    /** FT-1107: THE CLOCK-FACE FORM — the ask standing in the town readout,
     *  with the ring itself as its control. Wins over `pinned`. */
    face: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // FT-1005: the free-text draft — local while typing (the input cannot
      // ride the wire round trip per keystroke), sent debounced and on blur.
      // null = not dirty, show what the host holds.
      textDraft: null,
    };
  },
  computed: {
    ...mapState("players", ["players"]),
    // FT-1399: for the verb lines' "yourself" — a pick that is the player's
    // own chair (the Imp's starpass) is named as the act it is, not as a
    // third person who happens to share their name.
    ...mapState(["session"]),
    moonFull() {
      return moonFull;
    },
    /** FT-1272: the header's icon — golem/roleIcon owns the three-source
     *  lookup (forged artwork, the shipped PNG, a borrowed one). */
    iconUrl() {
      return roleIconUrl(this.action.role, this.$store.getters.rolesJSONbyId);
    },
    /** FT-1272: the character's team, floored to one this face has a colour
     *  for. A traveller or a fabled reaching this ask keeps its own token
     *  (both are in the map below); anything else reads as townsfolk rather
     *  than as an unstyled name. */
    teamOf() {
      const t = (this.action.role && this.action.role.team) || "";
      return TEAMS.includes(t) ? t : "townsfolk";
    },
    /**
     * FT-1291: THE STORYTELLER HAS ANSWERED AND SENT IT — so this seat's
     * choice is closed.
     *
     * Read off the HOST's echoed row, never decided here: `sent` is projected
     * onto a player's own row by golem/nightLog and arrives with every night
     * frame. That is what makes the lock follow the row in BOTH directions —
     * a reopen clears the storyteller's tick, the next frame carries `sent`
     * false, and these controls come live again with no second flag to keep
     * in step and nothing latched on this side.
     *
     * The host refuses the write regardless (night/applyPlayerAction); this is
     * the honest face on that refusal, not the enforcement of it.
     */
    sent() {
      return !!(this.row && this.row.sent);
    },
    /** FT-1384: the staged view — all four read the night module's own
     *  getters (the FT-1101 one-truth rule), so the face and the coins can
     *  never disagree about what is staged, complete, or sealed. */
    stagedTargets() {
      return this.$store.getters["night/myStagedTargets"];
    },
    stageComplete() {
      return this.$store.getters["night/myStageComplete"];
    },
    confirmed() {
      return this.$store.getters["night/myConfirmed"];
    },
    /** Confirmed on this side, or answered on the host's — THE lock. */
    locked() {
      return this.$store.getters["night/myCallLocked"];
    },
    /** FT-1385: the standing telling (night/myTold), or null. */
    told() {
      return this.$store.getters["night/myTold"];
    },
    /** FT-1385: TONIGHT'S telling for THIS ask, dressed — or null. The day
     *  gate is what keeps the Empath's night-one sentence off night two's
     *  face while the new ask still stands unanswered. */
    toldTonight() {
      const told = this.told;
      if (!told || !hasToldArt(told.roleId)) return null;
      if (told.roleId !== this.action.role.id) return null;
      if (told.day !== this.$store.state.night.day) return null;
      return told;
    },
    toldSettled() {
      return !!this.toldTonight && this.toldTonight.phase === "settled";
    },
    /** FT-1385: the number-role dress on the answer chips — the Chef's
     *  numeral takes the scorch brand, the Empath's its heart halo (empty
     *  for everyone else, so every other role's chips render exactly as
     *  before). */
    toldNumberDress() {
      const told = this.toldTonight;
      if (!told || !this.face) return [];
      // FT-1403 phase 1: the registry's flag, not a role-id pair — the
      // class itself stays the role's own (nf-num-chef, nf-num-empath).
      // FT-1404: chip DRESS is signs; the chip and its number are not.
      if (!enabled() || !signFlag(told.roleId, "wantsNumeralBadge")) return [];
      return ["nf-num-" + told.roleId, { "num-settled": this.toldSettled }];
    },
    /** FT-1385: the Librarian's zero-Outsiders night — nothing delivered
     *  means nobody is it: the book opens on blank pages. */
    toldZero() {
      const told = this.toldTonight;
      return !!told && !told.targets.length && !told.characterName;
    },
    /** The Librarian's book renders under her sentence, both beats.
     *  FT-1403 phase 1: hers by registry flag now, not by id. */
    toldBook() {
      return (
        enabled() &&
        this.face &&
        !!this.toldTonight &&
        signFlag(this.toldTonight.roleId, "wantsBook")
      );
    },
    /** The telling's own words — bright at the telling, the smaller
     *  pair-naming line once settled. Empty when there is no telling. */
    toldSentenceText() {
      const told = this.toldTonight;
      if (!told || !this.face) return "";
      const names = toldSeats(told, this.players).map((seat) => {
        const p = this.players[seat];
        return (p && p.name) || "Seat " + (seat + 1);
      });
      // FT-1403 phase 1: through the copy layer — a script's own telling/
      // settled template wins where one is authored, toldInfo's composed
      // sentence everywhere else.
      return toldSentenceFor(told, names, this.toldSettled);
    },
    /**
     * FT-1291: ...and what the player is TOLD about it. One short sentence, in
     * the app's own voice, saying the two things a locked control has to say —
     * that the answer came, and that the choice it was about is settled.
     *
     * Past tense and no apology: nothing has gone wrong here. This is the
     * ordinary end of a night action, and the line reads like the end of one.
     *
     * FT-1330 (user): AND ON A RECEIVE-ONLY ROW, "ANSWERED" WAS A LIE. The
     * Poisoner picks a target and is told nothing; the Imp chooses a kill and
     * is told nothing — nothing was answered, the storyteller only RECEIVED.
     * So a receive-only ask (action.receiveOnly, read off the same schema
     * fact the night sheet's own Received button reads — nightExchange's
     * RECORDS) names what actually happened instead: "Storyteller received
     * your choice — <target name> is Poisoned." The effect phrase is the
     * ROLE'S OWN, authored in golem/nightInfo beside its label and line
     * (`received`, carried here on the action), never a per-role string in
     * this component; a receive-only role without one falls back to the
     * generic acknowledgement. Rows that DO answer keep the FT-1291 line —
     * for them "answered" is the truth.
     */
    sentLine() {
      // FT-1384 (house rule): the FT-1330 receive-only receipt sentences
      // STAND DOWN — the storyteller's tick on a receive-only row causes no
      // player-side change at all now; the role's own seal (played at the
      // player's Confirm) is the receipt, and the sealed line below is the
      // one wording both moments share. Kept, commented, per never-delete:
      //
      //   if (this.action.receiveOnly) {
      //     const names = this.chosenNames.join(" & ");
      //     if (!names) return "Storyteller received your choice.";
      //     const effect = this.action.received;
      //     return effect
      //       ? "Storyteller received your choice — " + names + " " + effect + "."
      //       : "Storyteller received your choice — " + names + ".";
      //   }
      if (this.action.receiveOnly || !this.sent) return this.sealedLine;
      return "The storyteller has answered — your choice stands.";
    },
    /** FT-1384: the sealed choice's one sentence — said at Confirm and
     *  unchanged by the storyteller's tick (ST-received is not a player-side
     *  state). Present tense, no apology: sealing is the ordinary end of a
     *  night action now.
     *
     *  FT-1399: a dressed chooser's seal names the act — "You protected
     *  Maeve", "You are serving Bram" — off its own verb template. The
     *  generic sentence stays for everyone else, and as the fallback for
     *  the edge where a verb role seals with no recorded pick to name. */
    /** FT-1403 phase 2: the invite template with its placeholder filled —
     *  the shipped six carry no `{names}`, so they read exactly as before,
     *  but a SCRIPT'S OWN invite may name the pick, which is what the copy
     *  layer's header has promised since phase 1. Padded ("…") for a slot
     *  not yet staged, the same shape the Confirm label uses, so the ask
     *  reads as a sentence from the first beat rather than as a gap. */
    inviteLine() {
      if (!this.verbs) return "";
      const tpl = this.verbs.invite;
      if (tpl.indexOf("{names}") === -1) return tpl;
      return tpl.replace(/\{names\}/g, this.verbNames(true));
    },
    sealedLine() {
      if (this.verbs) {
        const names = this.verbNames(false);
        if (names) return this.verbs.sealed.replace("{names}", names);
      }
      return "Your choice is sealed until the storyteller asks again.";
    },
    /** The free box shows the local draft while typing, the host's echo
     *  otherwise. */
    freeTextValue() {
      if (this.textDraft !== null) return this.textDraft;
      return this.row ? this.row.playerText : "";
    },
    /**
     * FT-1107: THE SEATS THIS PLAYER HAS PICKED, as the host recorded them,
     * named. Read off `targets` (slot-aligned seat indexes) rather than the
     * projection's `targetNames` — that list is compacted with `.filter
     * (Boolean)` and so cannot say WHICH slot a name came from, and a seat
     * renamed mid-game would keep the name it wore when the row was written.
     * The ring is showing these same seats lit; this line is what makes the
     * lit coins readable at a glance without counting round the clock.
     */
    chosenNames() {
      // FT-1384 (face form): the STAGED picks, so the chips move with the
      // player's taps before anything is sent — and the host's record once
      // the choice has gone out, which is what myShownTargets already
      // decides. The band form keeps its delivered-row read.
      const targets = this.face
        ? this.$store.getters["night/myShownTargets"]
        : (this.row && this.row.targets) || [];
      const out = [];
      targets.forEach((seat) => {
        if (!Number.isInteger(seat) || seat < 0) return;
        const p = this.players[seat];
        out.push((p && p.name) || "Seat " + (seat + 1));
      });
      return out;
    },
    /** How many of this player's own slots are still empty. */
    slotsLeft() {
      return Math.max(0, (this.action.slots || 0) - this.chosenNames.length);
    },
    /** FT-1399: this chooser's own verb templates, or null — null keeps
     *  every generic line exactly as it was (an undressed chooser, the
     *  family tier's roles until they author their own). FT-1403 phase 1:
     *  read through the copy layer, so a script's nightCopy can reword a
     *  verb — or dress an undressed chooser by authoring the whole pair. */
    verbs() {
      return verbsFor(this.action.role.id);
    },
    /** FT-1399: the picked SEATS behind the verb lines — the same source
     *  chosenNames reads (staged on the face, the delivered row in the
     *  band), kept as seats because "yourself" is a fact about a seat, not
     *  about a name. */
    verbSeats() {
      const targets = this.face
        ? this.$store.getters["night/myShownTargets"]
        : (this.row && this.row.targets) || [];
      return targets.filter((s) => Number.isInteger(s) && s >= 0);
    },
    /** FT-1399 (vet 2): the Confirm control's label — the ONE generic
     *  "Confirm {names}" for every dressed chooser ("Confirm Maeve"), live
     *  as the pick moves, "…" padding a slot not yet staged. Generic on
     *  purpose: a verb-confirm ("Kill Maeve") read as the act happening at
     *  the press when nothing has been sent, and doubled the name the
     *  chips already carried — the verb belongs to the invite and the
     *  sealed receipt (CHOOSER_VERBS' two templates). The bare word for a
     *  chooser with no verb entry, exactly as before. */
    confirmLabel() {
      if (!this.verbs) return "Confirm";
      return "Confirm " + this.verbNames(true);
    },
    /** FT-1399 vet 2 (user): DO THE NAME CHIPS RENDER? — a pick's name is
     *  never listed twice in one state. A dressed chooser's own lines
     *  carry the names in every state where a pick exists to name — the
     *  padded "Confirm Silas & …" mid-stage, the "Confirm Ines" button at
     *  stage complete, the "You protected Ines" seal — so its chips never
     *  render at all. (The states whose line names nobody — the fresh
     *  invite, the reopened ask — hold no picks either: myShownTargets is
     *  staged-only while unlocked, so the chips were already empty there.)
     *  An undressed chooser keeps its chips everywhere — its generic lines
     *  never name a pick, and the chips are its only naming. */
    chipsShown() {
      return !this.verbs;
    },
    /**
     * FT-1107: the one line that teaches the control and reports on it. It is
     * the imperative while there is picking left to do and a plain past-tense
     * statement once there is not — never a third state, because the hub is
     * two lines tall and a "done!" is a line that says nothing.
     */
    askLine() {
      // FT-1291: THE CLOSED ROW'S LINE COMES FIRST, ahead of every count —
      // the counts are instructions and there is nothing left to instruct.
      // FT-1384 widened the gate from `sent` to the shared lock: the line
      // lands at the player's own Confirm now, and sentLine itself decides
      // between "answered" (a role the storyteller answers) and "sealed"
      // (a receive-only role, or an answer still to come).
      if (this.locked) return this.sentLine;
      const left = this.slotsLeft;
      // FT-1384: every slot staged and nothing sent — the one moment the
      // face's job is to point at the Confirm standing right below it.
      // FT-1399: a dressed chooser says NOTHING here — its Confirm button
      // is wearing the whole sentence ("Confirm Maeve"), and a hint line
      // above a button that already says it would be the same words
      // twice. The template's own v-if drops the empty span.
      if (this.stageComplete) {
        if (this.verbs) return "";
        return this.action.slots > 1
          ? "Confirm — or select a cog to change one."
          : "Confirm — or select another cog to change it.";
      }
      // ONE LINE, ALWAYS. The hub's vertical budget is the gap between the
      // counts and the seat at six o'clock; a wrapped instruction pushed the
      // storyteller's answer down behind that coin (measured at 1280x900,
      // first pass). The chips below already say the choices are in, so the
      // sentence that said it too came out.
      // FT-1272 (user): "select" rather than "tap" — the app is played on
      // a mouse as often as a finger, and select names the act either way.
      // FT-1272 (user, second pass): and the thing you select is a COG. That
      // is the user's word for a player's own piece on the ring, so it is the
      // word the ask uses. The COINS this file still names in its comments are
      // the same objects seen from the art's side; the instruction is the one
      // place a player has to recognise what is being pointed at, so it takes
      // the vocabulary they speak.
      // FT-1399: the reopened state (the row's record still fills the
      // shown seats, nothing staged) follows the INVITE template again —
      // the night is asking afresh, so the line asks afresh.
      if (!left) {
        if (this.verbs) return this.inviteLine;
        return this.action.slots > 1
          ? "Select a cog to change one."
          : "Select another cog to change it.";
      }
      if (!this.chosenNames.length) {
        // FT-1399: the dressed chooser's invite names the act — "Select a
        // player to protect tonight" — instead of the control's geometry.
        if (this.verbs) return this.inviteLine;
        return this.action.slots > 1
          ? "Select " + this.action.slots + " players on the clock."
          : "Select a player on the clock.";
      }
      // FT-1399 (vet 2): a dressed chooser mid-stage reads its own progress
      // off the generic confirm sentence with "…" holding the unstaged slot
      // — the Fortune Teller's "Confirm Silas & …" — rather than the bare
      // "Select 1 more." The chips yield to this line too (chipsShown).
      if (this.verbs) {
        return "Confirm " + this.verbNames(true);
      }
      return "Select " + left + " more.";
    },
    /**
     * FT-1107: WHAT THE STORYTELLER HAS SAID BACK, as chips — the same four
     * delivered values the band renders inline (the yes/no, a number, a
     * character, their words), assembled here instead of as four v-ifs
     * because on the face they wrap onto their own line and need to be
     * countable (`v-if="answer.length"`) before the line is drawn at all.
     *
     * The lie mark is not here, and cannot be: `projectPlayerRow` never puts
     * `isFalseInfo` on a player's copy of their own row in the first place.
     */
    answer() {
      const row = this.row;
      if (!row) return [];
      const out = [];
      if (row.ping === true) out.push({ text: "Yes", cls: "yes" });
      else if (row.ping === false) out.push({ text: "No", cls: "no" });
      if (row.number !== null && row.number !== undefined && row.number !== "")
        out.push({ text: String(row.number), cls: "" });
      if (row.characterName) out.push({ text: row.characterName, cls: "" });
      if (row.text) out.push({ text: row.text, cls: "words" });
      return out;
    },
  },
  methods: {
    pingClass(row) {
      return { yes: row.ping === true, no: row.ping === false };
    },
    /** FT-1401 v2 (user): is this answer chip the Empath's own ZERO? —
     *  it renders as her heart (the ledger's glyph) instead of a "0".
     *  Only the bare number chip on her own face: `!a.cls` keeps the
     *  yes/no and free-words chips out, and toldTonight's day-gate keeps
     *  an older night's residue honest. */
    heartChip(a) {
      const told = this.toldTonight;
      // FT-1403 phase 1: the ledger's glyphs follow the wantsLedger flag.
      // FT-1404: they are dress — signs off, the chip says its plain "0".
      if (!enabled()) return false;
      if (!told || !signFlag(told.roleId, "wantsLedger") || !this.face)
        return false;
      return !a.cls && a.text === "0";
    },
    /** FT-1401 v4 (user): drips retired — red alone. `bloodChip` only ever
     *  gated the retired drip overlay (the ring/red numeral treatment is
     *  unconditional CSS on the count chip and never called it) so it is
     *  now dead weight for a retired feature; kept-commented per
     *  never-delete:
    bloodChip(a) {
      const told = this.toldTonight;
      if (!enabled()) return false;
      if (!told || !signFlag(told.roleId, "wantsLedger") || !this.face)
        return false;
      return !a.cls && a.text !== "0";
    },
    */
    /** FT-1399: one picked seat, named for a verb line — "yourself" when
     *  the pick is the player's own chair (the Imp's starpass door), the
     *  seat's display name otherwise. */
    verbName(seat) {
      if (seat === this.session.claimedSeat) return "yourself";
      const p = this.players[seat];
      return (p && p.name) || "Seat " + (seat + 1);
    },
    /** FT-1399: the verb templates' `{names}` — the picked seats' names,
     *  " & "-joined; `pad` fills each unstaged slot with "…" so a
     *  half-staged Fortune Teller reads "Confirm Maeve & …". */
    verbNames(pad) {
      const names = this.verbSeats.map((s) => this.verbName(s));
      if (pad) {
        while (names.length < (this.action.slots || 0)) names.push("…");
      }
      return names.join(" & ");
    },
    /**
     * FT-1005: the picker shows what the HOST recorded — the echo, never
     * local optimism. A pick answers back in one round trip (the host
     * re-sends this seat's rows on every action frame, applied or refused),
     * so the control settles on the truth even when a storyteller-entered
     * value stood and the pick was refused.
     */
    slotValue(i) {
      const row = this.row;
      if (row && Number.isInteger(row.targets[i])) return row.targets[i];
      return -1;
    },
    /** One pick, up the wire. The commit is the event (the callBack idiom);
     *  socket.js sends it direct to the host and stamps our playerId.
     *
     *  FT-1291: and it stops at a sent row. Belt and braces behind the
     *  picker's own `disabled`, the same shape NightSheet's `sendRow` keeps
     *  behind its button: this method is the component's only write path, and
     *  a later caller must not be able to route round the predicate. The host
     *  refuses it anyway — that is the truth; this only keeps the client from
     *  sending a frame it already knows will be turned down. */
    /**
     * FT-1384: THE CONFIRM PRESSED — the whole choice goes up as ONE frame.
     *
     * The payload is exactly the frame a single tap used to send (FT-1005's
     * slot-aligned targets; the host merge, the belief check on the way in,
     * the echo back — all untouched), carrying every staged slot at once.
     * Two commits, deliberately separate: playerAction is the WIRE event
     * (socket.js's subscription table sends on it) and confirmStage is the
     * local seal — a wire event that also mutated staging would put a send
     * inside state the table replays.
     *
     * The guard is the same belt the other write paths keep: the button's
     * own v-if is the courtesy, this is the fact, and the host's refusal of
     * a sent row remains the authority.
     */
    confirm() {
      if (this.locked || !this.stageComplete) return;
      this.$store.commit("night/playerAction", {
        roleId: this.action.role.id,
        targets: this.stagedTargets.slice(0, this.action.slots),
      });
      this.$store.commit("night/confirmStage");
    },
    pickSeat(i, seat) {
      if (this.sent) return;
      const targets = new Array(this.action.slots).fill(null);
      targets[i] = Number.isInteger(seat) ? seat : -1;
      this.$store.commit("night/playerAction", {
        roleId: this.action.role.id,
        targets,
      });
    },
    typeText(value) {
      this.textDraft = value;
      clearTimeout(this._textTimer);
      this._textTimer = setTimeout(this.flushText, 400);
    },
    /** FT-1291: the debounce can be MID-AIR when the Send lands — the box
     *  goes `disabled` at that instant but a 400ms timer is already running
     *  and blur fires on the way out. Both routes come through here, so the
     *  guard is here, once. (The watcher below drops the stranded draft so the
     *  disabled box shows the host's words rather than words that will never
     *  be sent.) */
    flushText() {
      clearTimeout(this._textTimer);
      if (this.sent) return;
      if (this.textDraft === null) return;
      this.$store.commit("night/playerAction", {
        roleId: this.action.role.id,
        text: this.textDraft,
      });
      // the draft stays on screen until the host's echo carries the same
      // words back — see the watcher below
    },
  },
  watch: {
    /** FT-1005: hand the free box back to the echo once it caught up.
     *
     *  FT-1291: ...and hand it back UNCONDITIONALLY once the row has been
     *  sent. A draft that was still local when the storyteller sent will never
     *  be committed now (the host refuses it), so leaving it on screen would
     *  show the player their own unsent words in a locked box and let them
     *  believe those words were what the answer was about. The echo is the
     *  truth; on a sent row it is the only thing there is. */
    row(row) {
      if (row && row.sent) {
        this.textDraft = null;
        clearTimeout(this._textTimer);
        return;
      }
      if (
        this.textDraft !== null &&
        row &&
        (row.playerText || "") === this.textDraft.slice(0, 280)
      ) {
        this.textDraft = null;
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this._textTimer);
  },
};
</script>

<style scoped lang="scss">
// FT-1113: the team palette, for the chips' blue — `$townsfolk` is the ONE
// blue this fork has and it is already on this very panel (TownInfo's
// townsfolk count and glyph wear it), so the chips take it rather than
// inventing a second one.
//
// FT-1272: ...and the same palette now dresses the header's NAME by team. The
// map is built FROM those variables rather than restating their hexes — this
// file is the third to want a team map (RoleHoverCard and the workbench each
// hold their own, both with the values typed out by hand), and a copy that
// reads the tokens cannot drift from them.
@import "../vars.scss";

$team-colors: (
  "townsfolk": $townsfolk,
  "outsider": $outsider,
  "minion": $minion,
  "demon": $demon,
  "traveler": $traveler,
  "fabled": $fabled,
);

// ── FT-1272: THE FACE'S PURPLE ───────────────────────────────────────────
//
// #a78fcd is the fork's PICK INK — the colour that already means "this is the
// one you are choosing" wherever a choice is being made on the square: the
// night-pick overlay on a cog (Player.vue's `.night-pick`, which is the very
// control this face's instruction is telling the player to use), the seat
// ring's focused edge (SeatRing's `--fd-edge-color`), and a whisper's picked
// seat (SeatWhisper). It is not a new value and it is not the plum control
// edge — the plum dresses SURFACES (a dropdown's rim, a well), and this is
// INK on a dial.
//
// It is one colour for both the instruction and the chosen-name chips on
// purpose: those two lines are the two halves of one sentence — what to do,
// and what has been done — and the face reads as one voice when they match.
$face-pick: #a78fcd;

// FT-1101: the FT-1005 nights-view furniture (.nd-row + the .nd-tonight
// overrides), moved here WHOLE with the markup it dresses. Scoped styles do
// not reach into a child component, so the look had to travel with the
// controls or the fold would have read as a redesign; the values below are
// ChroniclesNights's own, unchanged.
.nd-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  padding: 5px 8px;
  margin-bottom: 4px;
  background: rgba(255, 255, 255, 0.05);
  border-left: 3px solid rgb(var(--reader-deep-rgb));
  border-radius: 0 5px 5px 0;
  font-size: 90%;

  .nd-role {
    font-weight: bold;
  }
  .nd-told {
    padding: 0 8px;
    border-radius: 9px;
    font-weight: bold;
    background: rgba(0, 0, 0, 0.4);
    &.yes {
      color: #7ed67e;
    }
    &.no {
      color: #ff8a8a;
    }
  }
  .nd-text {
    flex-basis: 100%;
    opacity: 0.8;
    font-style: italic;
  }
}

// the gold seam: this is the one row the player is standing IN, not reading
// back
.nd-live {
  border-left-color: #b28f2f;
}

.nd-line {
  flex-basis: 100%;
  opacity: 0.75;
  font-style: italic;
}

.nd-input {
  flex-basis: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.nd-your {
  opacity: 0.75;
  font-size: 12.5px;
  white-space: nowrap;
}

.nd-free {
  height: 30px;
  flex: 1 1 140px;
  min-width: 0;
  font-family: inherit;
  font-size: 12.5px;
  color: white;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid #3d3d3d;
  border-radius: 5px;
  padding: 0 8px;
  &:focus-visible {
    outline: none;
    border-color: #b28f2f;
  }
  // FT-1291: a sent row's box. It KEEPS ITS WORDS at full strength — they are
  // what the storyteller answered about and the player still has to be able to
  // read them — and stands down the frame instead: no gold focus edge to
  // invite a click, a flatter ground, and the cursor says so on the way past.
  // Dimming the text would have hidden the record along with the control.
  &:disabled {
    cursor: default;
    opacity: 1;
    color: white;
    border-color: #303030;
    background: rgba(0, 0, 0, 0.32);
  }
}

@media (pointer: coarse) {
  .nd-free {
    height: 44px;
    font-size: 14px;
  }
}

// ── THE PINNED BAND — the stream's foot, where the player already is ──────
.nc.pinned {
  border: 1px solid rgba(178, 143, 47, 0.55);
  border-radius: 6px;
  background: rgba(24, 15, 34, 0.72);
  padding: 5px 7px 4px;
  margin: 4px 0 2px;
  flex: 0 0 auto;

  .nd-row {
    margin-bottom: 0;
  }
}

.nc-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.8;
  margin-bottom: 3px;
}

.nc-moon {
  width: 14px;
  height: 14px;
}

.nc-privacy {
  display: block;
  font-size: 11px;
  opacity: 0.55;
  padding: 3px 4px 0;
}

// ── FT-1107: THE FACE FORM — the ask, standing on the clock face ─────────
//
// It had NO FRAME, and the reasoning ran: every other panel in this app that
// carries a control also carries a plate, because a panel is a thing laid
// OVER the town — and this one is not laid over the town, it IS the town
// centre, so a bordered card there would read as a dialog dropped on the
// clock. The words floated on the dial the way the dial's own numerals do,
// with a text shadow doing the legibility work a ground would otherwise do.
//
// ── FT-1125 (user): AND IT HAS ONE NOW, BECAUSE THE PLATE IS NOT A CARD ──
//
// "visually it still doesn't show the disc for the player? only the
// storyteller?" The premise above was right about BORDERED CARDS and wrong
// about this face: the storyteller's checklist stands in this exact spot on a
// FACE DISC — a plate laid ON the dial, same centre, same border-radius as
// the art it sits on (src/faceDisc.scss) — which is the opposite object from
// a card floating over it. The player got nothing behind their ask, which is
// the asymmetry the user was pointing at.
//
// THE TEXT SHADOW STAYS. It is no longer the only thing doing the work, but
// the plate is glass rather than a fill (its own tint is zero — see that
// file's tint block), so the hand-painted art still varies underneath and a
// shadow still tracks it where a fixed ground would not.
//
// WIDTH, PLACEMENT AND NOW THE PLATE ARE NOT HERE. TownInfo.vue owns all
// three, because they are facts about the clock face, not about the ask.
.nc.face {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  text-align: center;
  color: white;
  text-shadow:
    0 0 4px black,
    0 0 10px black;
  line-height: 1.25;
}

// ── FT-1272: THE HEADER WEARS THE CHARACTER, AND ITS TEAM ────────────────
//
// It was #f0dcae — the parchment gold every other line on this face carries —
// so the one element that could have told a player which side they are on was
// saying nothing the neighbouring lines were not already saying.
//
// THE TREATMENT IS FT-1167'S, NOT A NEW ONE. That card had the same argument
// and settled it with measurements: the RAW team token as ink, and a dark halo
// under it doing the legibility work, rather than `mix(white, $color, 32%)` —
// because a wash pulls six hues a third of the way toward each other and
// telling a Minion from a Demon at a glance is the entire point of the colour.
// Measured against this face's effective ground (the halo's black, since the
// face disc's own tint is zero and the hand-painted dial varies underneath):
//
//   fabled     #ffe91f   16.35:1
//   outsider   #46d5ff   12.22:1
//   minion     #ff6900    7.27:1
//   traveler   #cc04ff    4.86:1
//   townsfolk  #1f65ff    4.36:1
//   demon      #ce0100    3.63:1   ← the floor
//
// AND THE SIZE MOVED WITH IT, for the same reason FT-1167 went 17 -> 19px on
// its own compact card. WCAG's 3:1 bar is for LARGE text, which starts at
// 18.66px bold; at 118% of this face's ambient type the name landed just
// under, which would have put the demon's #ce0100 against the 4.5:1 bar it
// does not clear. 132% carries it over the line on the disc, and the name was
// already the header — being a little louder costs the composition nothing.
//
// The halo is BLACK rather than each team's own colour: the answer chips below
// use a coloured halo to say "bright" (see `.nf-told`), and the header is not
// competing with them — it is being read.
.nf-role {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-family: PiratesBay, sans-serif;
  letter-spacing: 1px;
  font-size: 132%;
  color: #f0dcae;

  @each $team, $color in $team-colors {
    &.team-#{$team} {
      color: $color;
      text-shadow:
        0 1px 2px rgba(0, 0, 0, 0.95),
        0 0 6px rgba(0, 0, 0, 0.85);
    }
  }
}

// sized to the NAME'S OWN LINE (1em of the header's type, not a fixed pixel
// count) so the two stay proportioned when the disc scales the face's ambient
// size — the same reason nothing else in this block is in px.
.nf-role-icon {
  flex: 0 0 auto;
  width: 1.5em;
  height: 1.5em;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  // the art is painted on parchment and goes soft over a dark dial; the same
  // drop shadow the header's own type wears holds its edge
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
}

// tight on purpose — see `askLine`'s note on the hub's vertical budget
.nf-line {
  font-size: 78%;
  font-style: italic;
  opacity: 0.88;
  line-height: 1.2;
}

// THE INSTRUCTION LINE — the ONE line on the face that is an imperative and
// not a statement.
//
// IT WAS THE GOLD SEAM (#e2be62, the colour the night's own live row wears in
// the band form). FT-1272 (user) moves it to the pick ink, and the reason is
// that gold on this face had stopped meaning anything in particular: the role
// name was gold, the chips were gold before FT-1113, the seam is gold, the
// dial's own numerals are gold. Purple is the colour a player is already being
// asked to LOOK FOR — the cog they are about to select lights in exactly this
// hue — so the sentence telling them to select one now matches the thing it
// is pointing at.
//
// Measured on the halo's black ground (11.78:1 for the old gold, and 82% of
// the face's ambient type is ordinary-size text answering to 4.5:1):
//
//   #a78fcd   7.45:1   ← comfortably over, dimmer than the gold but not close
//
// It stays BOLD while there is picking left to do; `.told` below stands it
// down to normal weight once the answer has landed, unchanged.
.nf-ask {
  font-size: 82%;
  color: $face-pick;
  font-weight: bold;
}

// ── FT-1113: THE HIERARCHY INVERTS ONCE THE ANSWER LANDS ─────────────────
//
// Reading order and visual order stop agreeing here, on purpose. The role,
// the ability line and the instruction come FIRST because that is the order
// a player needs them in — who am I, what do I do, do it — and each one is
// spent the moment it has been read. The answer comes LAST in the markup and
// is the entire point of the turn, so once it exists the three lines above it
// stand back and let it be the loudest thing on the dial.
//
// The character's NAME does not dim: it is the only line that says whose
// answer this is, and an unlabelled "Yes" glowing on a clock face is a riddle.
// The two that do dim are both instructions, and an instruction that has
// already been followed is the cheapest ink on the face.
.nc.face.told {
  .nf-line {
    opacity: 0.55;
  }
  .nf-ask {
    opacity: 0.6;
    font-weight: normal;
  }
}

// ── FT-1291: THE SETTLED LINE ────────────────────────────────────────────
//
// The ask line's last state, and it is the one state that must NOT take the
// dimming above. `.told` stands the instruction down because an instruction
// already followed is spent — but this sentence is not an instruction, it is
// the ANSWER TO "why did the ring stop responding to me", and a player only
// reads it at the moment they try to change a pick and cannot. Dimmed to 0.6
// it would be the quietest thing on the face at exactly the moment it is the
// most wanted. The specificity beats `.nc.face.told .nf-ask` on the extra
// class, so it stands whether or not an answer chip is showing.
//
// It leaves the PICK INK behind, and that is the whole message in a colour:
// $face-pick means "this is the one you are choosing" everywhere it appears
// on this square (the cog overlay, the seat ring's edge, the name chips), and
// there is no choosing left. Parchment is the face's ordinary speaking voice.
// Readable, not loud — the storyteller's answer is still the loudest thing on
// the dial, which is the hierarchy FT-1113 built and this does not disturb.
.nf-ask.settled {
  color: #f6dfbd;
  opacity: 0.82;
  font-weight: normal;
}

// The band form's twin, on its own line under a row of dead pickers. Same
// sentence, same job — the band has no ask line of its own to reuse, so it
// gets the element the face gets the state.
.nd-settled {
  flex: 1 1 100%;
  font-size: 12px;
  color: #f6dfbd;
  opacity: 0.8;
}

.nf-chosen {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  margin-top: 1px;
}

// a picked seat's NAME.
//
// IT USED TO BE GOLD, dressed as the coin's own lit ring is (Player.vue's
// `.night-picked`) so the chip and the lit coin read as the same act. FT-1113
// (user: "keep the name chips. make them blue though") takes the blue, and
// the pairing with the coin is what it costs — written down rather than left
// to be rediscovered as a bug. What it buys is separation: the instruction
// above these is gold, the answer below them is green or red, and a third
// gold thing between two loud ones was the one row on the face with no colour
// of its own.
//
// THE BLUE WAS `$townsfolk` (#1f65ff, src/vars.scss) — the fork's only blue,
// and one already on this panel: TownInfo's own townsfolk count and glyph
// wear it, four lines under where these chips now stand.
//
// ── FT-1272 (user): AND THE BLUE HAD TO GO, FOR A REASON FT-1113 COULD ──
//    NOT HAVE SEEN COMING.
//
// $townsfolk is a TEAM colour, and as of this same pass the header directly
// above these chips is painted in the character's own team colour. A townsfolk
// blue on the chips under a blue name would have read as "these seats are
// townsfolk" — which is the one thing a night pick must never say. It is the
// exact leak the seat pickers were scrubbed of in FT-1150: a control that
// composes what somebody is TOLD may not render what anybody IS.
//
// So the chips take the pick ink instead. That also restores the pairing
// FT-1113 knowingly spent: the cog lights purple when it is picked (Player's
// `.night-pick`), and now the chip naming that cog is the same colour — chip
// and lit cog read as one act again, and this time without a team's hue
// anywhere near a seat.
//
// The NAME stays white rather than taking the purple as ink, unchanged from
// FT-1113's reasoning: a seat's name at 78% type is the one thing on this row
// that has to be READ rather than recognised, so the ring and the wash carry
// the colour and the letters stay at full contrast.
.nf-chip {
  font-size: 78%;
  padding: 0 7px;
  border-radius: 9px;
  background: rgba($face-pick, 0.28);
  border: 1px solid rgba($face-pick, 0.95);
  color: #fff;
}

// ── FT-1113: THE ANSWER IS THE LOUDEST THING ON THE FACE ─────────────────
//
// It was 90%-ish bold text in a small dark pill under the chips, and it is
// the whole reason the player was asked anything: the Fortune Teller's "Yes",
// the Empath's number, the character an Undertaker was shown. It is now the
// biggest and brightest element in the composition — bigger than the role
// name (118%), which was the previous top of the type scale.
//
// PIRATESBAY, because this app's display face is what it already reaches for
// when an element is meant to be READ FROM ACROSS THE TABLE (the role name
// right above it, the Start and End-night buttons, the winner banner). A
// 230% Roboto Condensed is merely large; the display face is the app saying
// "this one".
//
// THE COLOUR LANGUAGE IS UNTOUCHED — #7ed67e for yes and #ff8a8a for no are
// the exact hexes the band form has carried since FT-1005, and they stay to
// the byte. What is added is a HALO in each answer's own colour, which is how
// everything else on this dial (the count glyphs, the team digits) says
// "bright" against hand-painted art that varies underneath.
.nf-told {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 4px 8px;
  margin-top: 4px;

  // restated rather than inherited: the band's `.nd-told` is nested inside
  // `.nd-row`, which the face form does not have
  .nd-told {
    font-family: PiratesBay, sans-serif;
    letter-spacing: 1px;
    font-size: 230%;
    line-height: 1.1;
    padding: 0 12px;
    border-radius: 10px;
    font-weight: bold;
    color: #fff;
    background: rgba(0, 0, 0, 0.42);
    text-shadow:
      0 0 5px black,
      0 0 12px black;
    &.yes {
      color: #7ed67e;
      text-shadow:
        0 0 5px black,
        0 0 14px rgba(126, 214, 126, 0.75);
    }
    &.no {
      color: #ff8a8a;
      text-shadow:
        0 0 5px black,
        0 0 14px rgba(255, 138, 138, 0.75);
    }
    // THE ONE ANSWER THAT DOES NOT GROW. A character's own free words are a
    // sentence, not a token — set at 230% display type it wraps to three
    // lines and pushes itself off the dial. It keeps the old pill, one step
    // up in size, and the loudness rule is answered by the fact that nothing
    // else on the face is a sentence either.
    &.words {
      font-family: inherit;
      letter-spacing: normal;
      font-size: 95%;
      font-weight: normal;
      font-style: italic;
      padding: 0 10px;
      text-shadow:
        0 0 4px black,
        0 0 10px black;
    }
  }
}

// ── FT-1385: THE TELLING'S SENTENCE ──────────────────────────────────────
//
// The told-information roles' answer, wearing its meaning: an italic line
// in a stitched oval under the delivered chip while the telling is bright
// ("one of these two is the Chef"), stepping down to small quiet type that
// NAMES the pair once the dress settles ("one of Dee & Hal — the Chef").
// The pill border takes each role's own ink (the per-role classes below)
// so the sentence, the coin rings and the thread read as one act. The
// transition carries the step-down — the settle is a relaxation, not a
// swap.
.nf-sentence {
  font-size: 84%;
  font-style: italic;
  color: #f6dfbd;
  padding: 0 12px 1px;
  border-radius: 999px;
  border: 1px solid rgba(240, 220, 174, 0.75);
  background: rgba(0, 0, 0, 0.35);
  margin-top: 3px;
  opacity: 0;
  animation: nfs-arrive 0.5s ease-out 0.25s both;
  transition:
    font-size 0.7s ease,
    opacity 0.7s ease,
    border-color 0.7s ease,
    background 0.7s ease,
    color 0.7s ease;

  // the residue's line: smaller, unboxed, quiet — but still the one line
  // that names the pair, so it stays comfortably readable.
  &.settled {
    font-size: 72%;
    border-color: transparent;
    background: transparent;
    opacity: 0.78;
    animation: none;
  }
}

@keyframes nfs-arrive {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// each told role's ink on the pill — the same hex family the coin marks
// and the thread wear (NightMark / NightThread), per role as art lands.
.nfs-washerwoman {
  border-color: rgba(234, 242, 255, 0.8);
  color: #f4f8ff;
  &.settled {
    border-color: transparent;
    color: #dfe9fb;
  }
}

.nfs-librarian {
  border-color: rgba(232, 217, 168, 0.8);
  color: #f3ead0;
  &.settled {
    border-color: transparent;
    color: #e8dcb8;
  }
}

.nfs-investigator {
  border-color: rgba(102, 149, 255, 0.8);
  color: #d9e6ff;
  &.settled {
    border-color: transparent;
    color: #b9c9f0;
  }
}

.nfs-chef {
  border-color: rgba(255, 179, 71, 0.8);
  color: #ffe9c9;
  &.settled {
    border-color: transparent;
    color: #f0d5a8;
  }
}

.nfs-empath {
  border-color: rgba(255, 159, 208, 0.8);
  color: #ffe3f1;
  &.settled {
    border-color: transparent;
    color: #f2c6dd;
  }
}

// FT-1402: the Grandmother — spectacle-brass gold, NightMark's $gm-gold.
.nfs-grandmother {
  border-color: rgba(224, 180, 92, 0.8);
  color: #f5e3bd;
  &.settled {
    border-color: transparent;
    color: #e6d0a0;
  }
}

// FT-1412: the Undertaker — headstone silver, NightMark's $ut-stone.
.nfs-undertaker {
  border-color: rgba(184, 196, 207, 0.8);
  color: #e9eef3;
  &.settled {
    border-color: transparent;
    color: #c9d2da;
  }
}

// ── FT-1385: THE CHEF'S BRAND ON THE NUMERAL ─────────────────────────────
// The count never sits bare: the chip goes ROUND and takes the scorch —
// seared on once at the telling (a flare of heat that cools into the held
// ring), a dotted char mark once settled. Ring + words + pair-mark on the
// coin are one object; this is the ring's centre half.
.nf-told.nf-num-chef .nd-told {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  min-width: 1.45em;
  padding: 0 0.28em;
  // FT-1400 vet: TRULY centred — PiratesBay's digit ink rides 0.095em low
  // in its em box (measured: ink centre 5px under the ring's at 53px), so
  // flex-centring the line box left the numeral visibly sunk. The bottom
  // padding re-biases the content box up by half itself; width and the
  // ring's circle are untouched (min-width + aspect-ratio own both).
  padding-bottom: 0.19em;
  border-radius: 50%;
  border: 2px solid #ffb347;
  color: #ffd98a;
  animation: nf-sear 0.9s ease-out both;
  transition:
    border-color 0.7s ease,
    box-shadow 0.7s ease,
    color 0.7s ease;
}

.nf-told.nf-num-chef.num-settled .nd-told {
  border-style: dotted;
  border-color: rgba(255, 179, 71, 0.65);
  box-shadow: none;
  color: #f4cf95;
  animation: none;
}

// ── FT-1385: THE EMPATH'S HALO ON THE NUMERAL ────────────────────────────
// The number takes its ring and beats up once with the sentence.
// FT-1401 v2 (user): the ring and the numeral go DRIED BLOOD — the coin
// ledger's own ink (NightMark's $em-led-red, one hex both surfaces), the
// ring SOLID both beats to match the ledger's framing (the old pale pink
// dotted settle retires). The ink-bias centring fix stays untouched.
$em-blood: #c01515;

.nf-told.nf-num-empath .nd-told {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  min-width: 1.45em;
  padding: 0 0.28em;
  // FT-1401 v4 (user): drips retired — red alone. `position: relative`
  // existed only to seat the now-retired blood-run overlay (below); no
  // longer needed since nothing on this box is absolutely positioned.
  // the same 0.095em ink-low correction as the Chef's ring above — one
  // font, one bias, both count chips.
  padding-bottom: 0.19em;
  border-radius: 50%;
  border: 2px solid $em-blood;
  color: $em-blood;
  // the deep red needs its lift off the dark pill: a warm text glow does
  // what the old hot-pink ink did.
  text-shadow:
    0 0 5px black,
    0 0 8px rgba(240, 96, 96, 0.55);
  animation: nf-halo 0.9s ease-out both;
  transition:
    border-color 0.7s ease,
    box-shadow 0.7s ease,
    color 0.7s ease;
}

.nf-told.nf-num-empath.num-settled .nd-told {
  border-color: rgba(192, 21, 21, 0.7);
  box-shadow: none;
  animation: none;
}

// FT-1401 v4 (user): drips retired — red alone. The centre count's blood
// run (pooled baseline + drawn drips) kept-commented per never-delete:
// .nf-told.nf-num-empath .nd-told .nd-blood {
//   position: absolute;
//   inset: 0;
//   width: 100%;
//   height: 100%;
//   overflow: visible;
//   pointer-events: none;
//   filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.9));
//
//   .ndb-drip,
//   .ndb-drop {
//     fill: $em-blood;
//   }
//   .ndb-dtip {
//     fill: #7d0e0e;
//   }
// }

// FT-1401 v2: the zero chip carries the HEART, not a numeral — the ring,
// pill and halo stand down and the ledger's glyph takes the chip's size.
.nf-told.nf-num-empath .nd-told.nd-heart {
  border: none;
  background: none;
  padding: 0;
  min-width: 0;
  aspect-ratio: auto;
  box-shadow: none;
  animation: none;

  .nd-heart-glyph {
    display: block;
    width: 1.45em;
    height: 1.45em;
    overflow: visible;

    // FT-1401 v4 (user): r2 heart back. Round 3's grown-copy inks
    // (NightMark's em-led-heart verbatim) kept-commented per
    // never-delete:
    // path {
    //   fill: #401428;
    //   stroke: #ff9fd0;
    //   stroke-width: 2.1;
    //   stroke-linejoin: round;
    //   filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
    //     drop-shadow(0 0 8px rgba(255, 159, 208, 0.6));
    // }
    // what stands: round 2's own heart ink, its original weight.
    path {
      fill: #401428;
      stroke: #ff9fd0;
      stroke-width: 1.1;
      stroke-linejoin: round;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
        drop-shadow(0 0 4px rgba(255, 159, 208, 0.65));
    }
  }
}

.nf-told.nf-num-empath.num-settled .nd-told.nd-heart {
  opacity: 0.85;
}

@keyframes nf-halo {
  0% {
    box-shadow: 0 0 0 rgba(192, 21, 21, 0);
  }
  40% {
    box-shadow: 0 0 20px rgba(192, 21, 21, 0.9);
  }
  100% {
    box-shadow: 0 0 11px rgba(192, 21, 21, 0.5);
  }
}

// the sear: one flare of heat that traces on and cools to the held glow.
@keyframes nf-sear {
  0% {
    box-shadow:
      0 0 0 rgba(255, 179, 71, 0),
      inset 0 0 0 rgba(255, 179, 71, 0);
  }
  45% {
    box-shadow:
      0 0 22px rgba(255, 179, 71, 0.95),
      inset 0 0 12px rgba(255, 179, 71, 0.55);
  }
  100% {
    box-shadow:
      0 0 12px rgba(255, 179, 71, 0.55),
      inset 0 0 7px rgba(255, 179, 71, 0.3);
  }
}

// ── FT-1385: THE LIBRARIAN'S BOOK ────────────────────────────────────────
// Sepia furniture, one blue ribbon — NightMark's own librarian inks, so the
// book, the page-corners on the coins and the ribbon between them read as
// one object. Open at the telling (fans up once), closed at the settle
// (the template swap IS the shut; the closed group's one fade is the soft
// thump), the bookmark left out.
.nf-book {
  width: 60px;
  height: 30px;
  margin-top: 2px;
  overflow: visible;

  .bk-page,
  .bk-cover {
    fill: rgba(58, 46, 20, 0.85);
    stroke: #e8d9a8;
    stroke-width: 1.4;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(232, 217, 168, 0.5));
  }
  .bk-page {
    fill: rgba(243, 234, 208, 0.28);
  }
  .bk-spine,
  .bk-pages {
    fill: none;
    stroke: #e8d9a8;
    stroke-width: 1.2;
    opacity: 0.85;
  }
  .bk-lines {
    fill: none;
    stroke: #f3ead0;
    stroke-width: 0.9;
    opacity: 0.65;
  }
  .bk-mark {
    fill: #2f5ac8;
    stroke: #e8d9a8;
    stroke-width: 0.8;
    stroke-linejoin: round;
  }

  .bk-open {
    animation: bk-fan 0.55s ease-out both;
  }
  .bk-closed {
    animation: bk-shut 0.45s ease-in both;
  }
  &.settled {
    opacity: 0.8;
  }
}

@keyframes bk-fan {
  from {
    opacity: 0;
    transform: translateY(4px) scaleY(0.4);
  }
  to {
    opacity: 1;
    transform: translateY(0) scaleY(1);
  }
}

@keyframes bk-shut {
  0% {
    opacity: 0.4;
    transform: scaleY(1.3);
  }
  70% {
    opacity: 1;
    transform: scaleY(0.95);
  }
  100% {
    opacity: 1;
    transform: scaleY(1);
  }
}

// ── FT-1384: THE CONFIRM — large, at the face centre ─────────────────────
//
// It stands where the vote hands stand (Vote.vue's .vo-solo slot, the other
// surface that puts a player's commit at the dial's middle), and it is the
// LOUDEST control this face ever carries, because it is the only one: the
// display face at .vo-solo's own 2em scale, on the pick ink's plate. The
// pick ink and not the gold, for FT-1272's reason — purple is what "the one
// you are choosing" already means on this square, and this button is that
// choice becoming final. pointer-events restated because TownInfo's plate
// switches them off wholesale and re-arms only the controls (its ::v-deep
// rule names this class).
.nf-confirm {
  pointer-events: auto;
  margin-top: 7px;
  padding: 0.05em 0.75em 0.12em;
  font-family: PiratesBay, sans-serif;
  font-size: 200%;
  letter-spacing: 2px;
  color: #f0dcae;
  cursor: pointer;
  background: rgba(24, 15, 34, 0.82);
  border: 2px solid $face-pick;
  border-radius: 12px;
  text-shadow:
    0 0 4px black,
    0 0 10px black;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.75),
    0 0 16px rgba(167, 143, 205, 0.45);
  transition:
    box-shadow 150ms,
    background 150ms,
    color 150ms;

  &:hover,
  &:focus-visible {
    outline: none;
    color: #fff;
    background: rgba(167, 143, 205, 0.22);
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.75),
      0 0 22px rgba(167, 143, 205, 0.75);
  }
}

.nf-free {
  // THE FLEX OVERRIDE IS THE POINT OF THIS RULE. `.nd-free` above says
  // `flex: 1 1 140px`, which reads as a 140px WIDTH inside the band's row —
  // and as a 140px HEIGHT here, because the face form is a COLUMN. Left
  // alone it drew a 130px-tall box and shoved the storyteller's answer down
  // behind the seat at six o'clock (measured, first pass).
  flex: 0 0 auto;
  height: 30px;
  width: 100%;
  max-width: 230px;
  margin-top: 3px;
  text-align: center;
}
</style>
